import {
  RiAlertLine,
  RiCheckLine,
  RiCloseCircleLine,
  RiErrorWarningLine,
  RiFileCopyLine,
  RiLoader4Line,
} from "react-icons/ri";
import { DIRECTIONS, DirectionCode } from "./photoModel";
import { RollupStats } from "./treeModel";
import { useMenu } from "./useMenu";

/**
 * Every state on this screen is carried by a tonal ground, an icon and a word — never by
 * colour alone, so a chip survives a colour-blind reader and a monochrome print.
 */
const CHIP = "inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium leading-5";

export const TONES = {
  ready: "bg-emerald-100 text-emerald-700",
  warn: "bg-amber-100 text-amber-800",
  error: "bg-red-100 text-red-700",
  dup: "bg-orange-100 text-orange-800",
  count: "bg-gray-100 text-gray-700",
  info: "bg-blue-50 text-blue-700",
} as const;

export function Chip({
  tone,
  icon,
  children,
  title,
  className,
}: {
  tone: keyof typeof TONES;
  icon?: JSX.Element;
  children: React.ReactNode;
  title?: string;
  className?: string;
}) {
  return (
    <span className={`${CHIP} ${TONES[tone]} ${className ?? ""}`} title={title}>
      {icon}
      {children}
    </span>
  );
}

/**
 * On a narrow screen a chip keeps its icon and its number and gives the word back to the
 * row — the identifiers are what the uploader came for, and a chip must never be the
 * reason a barcode gets truncated. The word stays in the accessibility tree either way.
 */
function Word({ children }: { children: React.ReactNode }) {
  return <span className="sr-only sm:not-sr-only">{children}</span>;
}

/**
 * Roll-up chips for a folder row: what is left to do, then what is already fine.
 * A settled branch says it once — "12 รูปพร้อม" carries the count, so no bare count
 * chip sits beside it repeating the same number.
 */
export function StateChips({ stats }: { stats: RollupStats }) {
  const settled = stats.unknown === 0 && stats.dup === 0 && stats.invalid === 0 && stats.failed === 0;
  return (
    <>
      {!settled && <Chip tone="count">{stats.photos} รูป</Chip>}
      {stats.invalid > 0 && (
        <Chip tone="error" icon={<RiCloseCircleLine size={13} aria-hidden="true" />}>
          <Word>ไม่พบสินค้า</Word>
        </Chip>
      )}
      {stats.unknown > 0 && (
        <Chip tone="warn" icon={<RiAlertLine size={13} aria-hidden="true" />}>
          <span className="tabular-nums">{stats.unknown} รูป</span>
          <Word>ยังไม่รู้มุม</Word>
        </Chip>
      )}
      {stats.dup > 0 && (
        <Chip tone="dup" icon={<RiFileCopyLine size={13} aria-hidden="true" />}>
          <span className="tabular-nums">{stats.dup} รูป</span>
          <Word>ซ้ำของเดิม</Word>
        </Chip>
      )}
      {stats.failed > 0 && (
        <Chip tone="error" icon={<RiErrorWarningLine size={13} aria-hidden="true" />}>
          <span className="tabular-nums">{stats.failed} รูป</span>
          <Word>ล้มเหลว</Word>
        </Chip>
      )}
      {settled && stats.checking > 0 && (
        <Chip tone="count" icon={<RiLoader4Line size={13} className="animate-spin" aria-hidden="true" />}>
          <Word>ตรวจสอบ…</Word>
        </Chip>
      )}
      {settled && stats.checking === 0 && stats.photos > 0 && (
        <Chip tone="ready" icon={<RiCheckLine size={13} aria-hidden="true" />}>
          <span className="tabular-nums">{stats.photos} รูป</span>
          <Word>พร้อม</Word>
        </Chip>
      )}
    </>
  );
}

export interface AngleControlProps {
  direction: DirectionCode | null;
  onSetDirection(d: DirectionCode): void;
  /** Labels the picker for screen readers. */
  fileLabel: string;
  disabled?: boolean;
  /** Roving tabindex: only the focused row's controls are in the tab order. */
  tabIndex?: number;
}

/**
 * The one job this screen exists for: an unreadable angle is a button, not a warning —
 * the fix is one click away from the row it belongs to.
 */
export function AngleControl({ direction, onSetDirection, fileLabel, disabled, tabIndex }: AngleControlProps) {
  const { open, setOpen, close, trigger, menu, onMenuKeyDown } = useMenu();
  const def = DIRECTIONS.find((d) => d.code === direction);

  if (direction && def) {
    return (
      <span
        className={`${CHIP} ${TONES.info} w-10 justify-center whitespace-nowrap tabular-nums sm:w-[5.5rem]`}
        title={`${def.code} → ${def.angle} (${def.label})`}
      >
        {def.code}
      </span>
    );
  }

  return (
    <span className="relative">
      <button
        ref={trigger}
        type="button"
        tabIndex={tabIndex}
        disabled={disabled}
        onClick={() => setOpen(!open)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={`เลือกมุมของ ${fileLabel}`}
        className={`${CHIP} ${TONES.warn} w-10 justify-center whitespace-nowrap transition-colors hover:bg-amber-200 sm:w-[5.5rem] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-600 focus-visible:ring-offset-1 disabled:opacity-50`}
      >
        <RiAlertLine size={13} aria-hidden="true" />
        <span className="sr-only sm:not-sr-only">เลือกมุม</span>
      </button>
      {open && (
        <span
          ref={menu as React.RefObject<HTMLSpanElement>}
          role="menu"
          aria-label={`มุมของ ${fileLabel}`}
          onKeyDown={onMenuKeyDown}
          className="absolute right-0 top-full z-50 mt-1 grid w-40 grid-cols-2 gap-1 rounded-lg border border-gray-200 bg-white p-1.5 shadow-[0_10px_15px_-3px_rgba(0,0,0,.1),0_4px_6px_-4px_rgba(0,0,0,.1)]"
        >
          {DIRECTIONS.map((d) => (
            <button
              key={d.code}
              type="button"
              role="menuitem"
              title={`${d.code} → ${d.angle}`}
              onClick={() => {
                onSetDirection(d.code);
                close();
              }}
              className="rounded px-2 py-1 text-left text-xs font-medium text-gray-700 transition-colors hover:bg-blue-50 hover:text-blue-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-600"
            >
              <span className="font-bold tabular-nums">{d.code}</span>{" "}
              <span className="text-gray-600">{d.label}</span>
            </button>
          ))}
        </span>
      )}
    </span>
  );
}
